import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type CSSProperties,
    type Dispatch,
    type DragEvent,
    type SetStateAction,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CornerDownLeft,
    FileText,
    Image as ImageIcon,
    Loader2,
    Paperclip,
    Plus,
    Search,
    X,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { getApiErrorMessage } from '@/lib/http';
import { isDefaultOrg, isManageV2Org, isSearchOnlyOrg } from '@/lib/orgGates';
import { RainbowBeamsBackdrop } from '@/components/RainbowBeamsBackdrop';
import DotMask from '@/components/DotMask';
import {
    agentApi,
    ATTACHMENT_MAX_BYTES,
    PER_TURN_ATTACHMENT_LIMIT,
    classifyFileMediaType,
    uploadAgentAttachment,
    type AgentAttachment,
} from '@/lib/agent.api';

// Set to true to re-enable the temporary live DotMask tuner panel.
const SHOW_DOTMASK_TUNER = false;

type ComposerSuggestion = { label: string; prompt: string };

const SUGGESTIONS: ComposerSuggestion[] = [
    { label: 'New role', prompt: 'Help me hire a ' },
    { label: 'Find candidates', prompt: 'Find candidates who ' },
    { label: 'Review pipeline', prompt: 'Walk me through the pipeline for ' },
    { label: 'Draft outreach', prompt: 'Draft an outreach message for ' },
];

const TALENT_SEARCH_SUGGESTIONS: ComposerSuggestion[] = [
    {
        label: 'Software engineers',
        prompt: 'Find software engineers in San Francisco with Python and Node.js experience at Series B companies',
    },
    {
        label: 'ERP sales leaders',
        prompt: 'Find sales managers in Dallas with ERP experience',
    },
    {
        label: 'German marketers',
        prompt: 'Find German-speaking marketing managers in Europe working at large enterprises',
    },
    {
        label: 'Consultants',
        prompt: 'Find consultants in London with 2+ years of experience at top consulting firms',
    },
];

/**
 * Claude-style centered greeting + chat composer. Used for both
 * Home (`/`) and New Tab (`/new-tab`) — opening either surface should
 * feel like "fresh canvas, type something". A new Chat object is only
 * minted on submit; the typed message rides along in router state and
 * is sent by ChatDetailPage post-hydration.
 *
 * File-upload nuance: there's no chatId until submit. So files picked
 * here are held as raw `File` handles + chip state, validated locally
 * for size/type. On submit we mint the chat, upload each file to its
 * id in parallel, then navigate with `initialMessage` AND
 * `initialAttachments` so the chat-detail page can fire the first turn
 * with the attachments already wired up.
 */
export function ChatComposerHero() {
    const { user, organization, ready } = useAuth();
    const navigate = useNavigate();
    const searchOnly = isSearchOnlyOrg(organization);
    // Default (no-preset) orgs get the pared-back Home: no search toggle,
    // attachments always on, and every submit lands in a /session/ chat.
    // Preset-flagged orgs (outreach, crypto, …) are unaffected — they keep
    // the manual search toggle below. Never true for search-only orgs (which
    // carry a preset), so the three branches stay mutually exclusive.
    const noPreset = isDefaultOrg(organization);
    // Search-only orgs are *always* in search mode. Preset orgs get a manual
    // toggle (rendered next to the composer footer) to opt in. The effective
    // `searchActive` flag is what drives suggestions, search routing, and the
    // footer hint.
    const [searchMode, setSearchMode] = useState(false);
    const searchActive = searchOnly || searchMode;
    // Attachments surface in search mode OR for default orgs (whose /session/
    // chat runs the general agent, which accepts attachments).
    const attachmentsEnabled = searchActive || noPreset;
    const suggestions = searchActive ? TALENT_SEARCH_SUGGESTIONS : SUGGESTIONS;
    const [draft, setDraft] = useState('');
    /**
     * The composer grows with what you type, up to its own `maxHeight` (240px),
     * then scrolls. Without this the box is fixed at `minHeight` and a
     * multi-line role description scrolls inside two visible lines.
     */
    const draftRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        const node = draftRef.current;
        if (!node) return;
        node.style.height = 'auto';
        node.style.height = `${Math.min(node.scrollHeight, 240)}px`;
    }, [draft]);
    const [creating, setCreating] = useState(false);
    const [stage, setStage] = useState<'idle' | 'creating' | 'uploading' | 'redirecting'>(
        'idle',
    );
    const [error, setError] = useState<string | null>(null);
    const [chips, setChips] = useState<HomeChip[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // TEMP: live-tuning controls for the DotMask backdrop. Remove the
    // `tune` state + <DotMaskTuner /> panel below once dialed in.
    const [tune, setTune] = useState<DotMaskTune>(DEFAULT_TUNE);

    const firstName = useMemo(() => {
        const first = user?.firstName?.trim();
        if (first) return first;
        return user?.email?.split('@')[0] ?? 'there';
    }, [user]);
    const greetingQuestion = searchOnly ? 'who are you looking for?' : 'how can I help?';
    const composerPlaceholder = searchOnly ? 'Who are you looking for?' : 'How can I help you today?';

    const validChips = chips.filter((c) => c.kind === 'pending') as Extract<
        HomeChip,
        { kind: 'pending' }
    >[];
    const hasErrorChip = chips.some((c) => c.kind === 'error');

    function pickFiles(files: FileList | File[]) {
        // File intake is gated to surfaces that show the paperclip (search
        // mode, or a default org's /session/ chat) — drag-drop must honor the
        // same gate, or a dropped file dead-ends a submit that can't carry it.
        if (!attachmentsEnabled) return;
        setError(null);
        const arr = Array.from(files);
        const room = PER_TURN_ATTACHMENT_LIMIT - chips.length;
        if (arr.length > room) {
            setError(
                `Up to ${PER_TURN_ATTACHMENT_LIMIT} attachments per message — drop ${
                    arr.length - room
                } and try again.`,
            );
        }
        const next: HomeChip[] = [];
        for (const file of arr.slice(0, room)) {
            const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const kind = classifyFileMediaType(file.type);
            if (!kind) {
                next.push({
                    kind: 'error',
                    localId,
                    filename: file.name,
                    message: 'Unsupported type — try PDF, image, .docx, .xlsx, or plain text.',
                });
                continue;
            }
            if (file.size > ATTACHMENT_MAX_BYTES[kind]) {
                const mb = Math.floor(ATTACHMENT_MAX_BYTES[kind] / (1024 * 1024));
                next.push({
                    kind: 'error',
                    localId,
                    filename: file.name,
                    message: `Too large — max ${mb} MB.`,
                });
                continue;
            }
            next.push({
                kind: 'pending',
                localId,
                file,
                fileKind: kind,
                isImage: kind === 'image',
                thumbDataUrl:
                    kind === 'image' ? URL.createObjectURL(file) : null,
            });
        }
        setChips((prev) => [...prev, ...next]);
    }

    function handleFileInput(e: ChangeEvent<HTMLInputElement>) {
        if (e.target.files && e.target.files.length > 0) {
            pickFiles(e.target.files);
        }
        e.target.value = '';
    }

    function handleDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            pickFiles(e.dataTransfer.files);
        }
    }

    function removeChip(localId: string) {
        setChips((prev) => {
            const removed = prev.find((c) => c.localId === localId);
            if (removed && removed.kind === 'pending' && removed.thumbDataUrl) {
                URL.revokeObjectURL(removed.thumbDataUrl);
            }
            return prev.filter((c) => c.localId !== localId);
        });
    }

    async function startChat() {
        const trimmed = draft.trim();
        if (!trimmed && validChips.length === 0) return;
        if (creating) return;
        // Auth must be resolved before the org-routed branch below (see
        // `canSubmit`, which disables send until then). Belt for programmatic
        // callers; never an error path for a human, whose button was disabled.
        if (!ready) return;
        if (hasErrorChip) {
            setError('Remove failed attachments first.');
            return;
        }

        setCreating(true);
        setError(null);

        try {
            // When search mode is active (search-only org, or a preset org
            // flipped the toggle) the home composer mints a *search* chat
            // (agentApi) and lands on the relevant search detail surface.
            if (searchActive) {
                setStage('creating');
                const chat = await agentApi.create(searchOnly ? { agentType: 'search_v3' } : {});

                let uploaded: AgentAttachment[] = [];
                if (validChips.length > 0) {
                    setStage('uploading');
                    uploaded = await Promise.all(
                        validChips.map((chip) =>
                            uploadAgentAttachment({ chatId: chat._id, file: chip.file }),
                        ),
                    );
                }

                setStage('redirecting');
                setDraft('');
                setChips([]);
                setCreating(false);
                navigate(`${searchOnly ? '/search-v3' : '/search'}/${chat._id}`, {
                    state: {
                        initialMessage: trimmed,
                        initialAttachments: uploaded,
                    },
                });
                return;
            }

            // Platform cohort: the session is backed by the platform gateway,
            // which creates a real ATS Job from this first message and lands in
            // manage-v2. There is no agent-worker chat to mint, so skip
            // straight to a presentation-only session id — minting one would
            // leave an orphaned chat behind on every submit.
            if (isManageV2Org(organization)) {
                setStage('redirecting');
                setDraft('');
                setChips([]);
                setCreating(false);
                navigate(`/session/${crypto.randomUUID()}`, {
                    state: { initialMessage: trimmed },
                });
                return;
            }

            // Stage 2.5 (job-manage): non-search submits land in an UNBOUND
            // general session — the agent can create a job mid-conversation
            // (create_job_role) and the chat moves into the new job's
            // workspace. Default orgs can attach files here (the general
            // agent accepts them); uploads run against the freshly-minted
            // chat, then ride into /session/ as initialAttachments.
            setStage('creating');
            const chat = await agentApi.create({ agentType: 'general' });

            let uploaded: AgentAttachment[] = [];
            if (validChips.length > 0) {
                setStage('uploading');
                uploaded = await Promise.all(
                    validChips.map((chip) =>
                        uploadAgentAttachment({ chatId: chat._id, file: chip.file }),
                    ),
                );
            }

            setStage('redirecting');
            setDraft('');
            setChips([]);
            setCreating(false);
            navigate(`/session/${chat._id}`, {
                state: { initialMessage: trimmed, initialAttachments: uploaded },
            });
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not start a new chat.'));
            setCreating(false);
            setStage('idle');
        }
    }

    function submit() {
        void startChat();
    }

    function pickSuggestion(prompt: string) {
        setDraft(prompt);
    }

    // `ready` gates submission because the branch below routes by organization,
    // which `useAuth` hydrates asynchronously: a submit before it lands would
    // send a manage-v2 org down the legacy path (or vice versa). Expressed as a
    // DISABLED send rather than an error on click — erroring would regress the
    // majority of orgs, whose legacy routing happens to be the pre-auth default.
    const canSubmit =
        ready &&
        !creating &&
        !hasErrorChip &&
        (draft.trim().length > 0 || validChips.length > 0);

    const sendButtonLabel =
        stage === 'creating'
            ? 'Starting…'
            : stage === 'uploading'
              ? 'Uploading…'
              : stage === 'redirecting'
                ? 'Opening…'
                : null;

    return (
        <main
            style={{
                position: 'relative',
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px clamp(16px, 4vw, 48px)',
                background: '#FAFAFA',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)',
                overflow: 'hidden',
                isolation: 'isolate',
            }}
        >
            <RainbowBeamsBackdrop style={{ top: tune.rainbowTop }} />
            {/* Flip to true to bring the live DotMask tuner back. */}
            {SHOW_DOTMASK_TUNER ? <DotMaskTuner tune={tune} setTune={setTune} /> : null}
            {/* Dot-shimmer backdrop: a soft oval over the heading + textbox.
                Sits below the content (zIndex 0 vs 1). Shape is a CSS mask so
                DotMask itself stays generic. */}
            <div
                aria-hidden
                style={{
                    position: 'absolute',
                    top: -tune.topOffset,
                    left: -tune.sideSpread,
                    right: -tune.sideSpread,
                    bottom: -tune.bottomSpread,
                    zIndex: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    WebkitMaskImage: `radial-gradient(ellipse ${tune.maskRX}% ${tune.maskRY}% at 50% ${tune.maskCY}%, #000 ${tune.fadeStart}%, transparent ${tune.fadeEnd}%)`,
                    maskImage: `radial-gradient(ellipse ${tune.maskRX}% ${tune.maskRY}% at 50% ${tune.maskCY}%, #000 ${tune.fadeStart}%, transparent ${tune.fadeEnd}%)`,
                }}
            >
                <DotMask
                    background="transparent"
                    color={tune.color}
                    accent={tune.accent}
                    accentMix={tune.accentMix}
                    gap={tune.gap}
                    dotShape={tune.dotShape}
                    dotWidth={tune.dotWidth}
                    dotHeight={tune.dotHeight}
                    density={tune.density}
                    speed={tune.speed}
                    vignette={tune.vignette}
                    style={{ height: '100%' }}
                />
            </div>
            <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ position: 'relative', alignSelf: 'center' }}>
                {/* Frosted halo behind the heading + logo so the text stays
                    legible over the dots/rainbow. Blur amount is tunable. */}
                {tune.headingBlur > 0 ? (
                    <div
                        aria-hidden
                        style={{
                            position: 'absolute',
                            inset: '-14px -40px',
                            zIndex: 0,
                            pointerEvents: 'none',
                            borderRadius: 9999,
                            backdropFilter: `blur(${tune.headingBlur}px)`,
                            WebkitBackdropFilter: `blur(${tune.headingBlur}px)`,
                            WebkitMaskImage:
                                'radial-gradient(75% 75% at 50% 50%, #000 45%, transparent 100%)',
                            maskImage:
                                'radial-gradient(75% 75% at 50% 50%, #000 45%, transparent 100%)',
                        }}
                    />
                ) : null}
                <h1
                    style={{
                        position: 'relative',
                        zIndex: 1,
                        margin: 0,
                        textAlign: 'center',
                        fontFamily: 'var(--font-serif)',
                        fontSize: 'clamp(28px, 4vw, 38px)',
                        fontWeight: 400,
                        letterSpacing: '-0.01em',
                        color: 'var(--text-primary)',
                    }}
                >
                    <img
                        src="https://d2cgute8qahbr9.cloudfront.net/lantern/lantern_logo_square.svg"
                        alt=""
                        aria-hidden
                        style={{
                            width: '1.2em',
                            height: '1.2em',
                            marginRight: 10,
                            verticalAlign: '-0.22em',
                            display: 'inline-block',
                        }}
                    />
                    <span
                        className="hero-greet__type"
                        style={
                            {
                                // Drive the typewriter clip-width off the real
                                // greeting length so longer names never get cut.
                                ['--greet-ch' as string]: `${firstName.length + greetingQuestion.length + 2}ch`,
                            } as CSSProperties
                        }
                    >
                        <span className="hero-greet__clip">
                            <span className="hero-greet__name">{firstName}</span>, {greetingQuestion}
                        </span>
                        <span
                            className="hero-greet__caret"
                            aria-hidden
                        />
                    </span>
                </h1>
              </div>

                <div
                    onDragEnter={(e) => {
                        if (e.dataTransfer.types.includes('Files')) {
                            e.preventDefault();
                            setDragOver(true);
                        }
                    }}
                    onDragOver={(e) => {
                        if (e.dataTransfer.types.includes('Files')) {
                            e.preventDefault();
                            setDragOver(true);
                        }
                    }}
                    onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                        setDragOver(false);
                    }}
                    onDrop={handleDrop}
                    style={{
                        background: dragOver ? 'rgba(253, 119, 94, 0.06)' : 'var(--surface-primary)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 18,
                        padding: 14,
                        boxShadow: '0 18px 50px -12px rgba(0, 0, 0, 0.14), 0 6px 16px -6px rgba(0, 0, 0, 0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        transition: 'background 100ms ease-out',
                    }}
                >
                    {chips.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {chips.map((chip) => (
                                <HomeChipView
                                    key={chip.localId}
                                    chip={chip}
                                    onRemove={() => removeChip(chip.localId)}
                                />
                            ))}
                        </div>
                    ) : null}
                    <textarea
                        ref={draftRef}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                        }}
                        placeholder={composerPlaceholder}
                        rows={2}
                        autoFocus
                        disabled={creating}
                        style={{
                            width: '100%',
                            resize: 'none',
                            minHeight: 56,
                            maxHeight: 240,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: 15,
                            lineHeight: 1.5,
                            fontFamily: 'inherit',
                            padding: '4px 6px',
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {!searchOnly && !noPreset ? (
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={searchMode}
                                    aria-label="Search mode"
                                    onClick={() => setSearchMode((v) => !v)}
                                    disabled={creating}
                                    title={
                                        searchMode
                                            ? 'Search mode on — submitting runs a new search'
                                            : 'Search mode off — submitting starts a new chat'
                                    }
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        height: 28,
                                        padding: '0 10px 0 8px',
                                        borderRadius: 9999,
                                        border: '1px solid var(--border-default)',
                                        background: searchMode
                                            ? 'rgba(253, 119, 94, 0.08)'
                                            : 'var(--surface-secondary)',
                                        color: searchMode
                                            ? 'var(--accent)'
                                            : 'var(--text-secondary)',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                        transition:
                                            'background 120ms, color 120ms, border-color 120ms',
                                    }}
                                >
                                    <span
                                        aria-hidden
                                        style={{
                                            position: 'relative',
                                            display: 'inline-block',
                                            width: 26,
                                            height: 15,
                                            borderRadius: 9999,
                                            background: searchMode
                                                ? 'var(--accent)'
                                                : 'var(--border-emphasis)',
                                            transition: 'background 120ms',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 2,
                                                left: searchMode ? 13 : 2,
                                                width: 11,
                                                height: 11,
                                                borderRadius: '50%',
                                                background: '#FFFFFF',
                                                transition: 'left 120ms',
                                            }}
                                        />
                                    </span>
                                    <Search size={12} strokeWidth={2} />
                                    Search
                                </button>
                            ) : null}
                            {attachmentsEnabled ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={creating || chips.length >= PER_TURN_ATTACHMENT_LIMIT}
                                        title={
                                            chips.length >= PER_TURN_ATTACHMENT_LIMIT
                                                ? `Up to ${PER_TURN_ATTACHMENT_LIMIT} attachments per message.`
                                                : 'Attach a file'
                                        }
                                        aria-label="Attach file"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: 8,
                                            border: '1px solid var(--border-default)',
                                            background: 'var(--surface-secondary)',
                                            color: 'var(--text-secondary)',
                                            cursor:
                                                creating || chips.length >= PER_TURN_ATTACHMENT_LIMIT
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                            opacity:
                                                creating || chips.length >= PER_TURN_ATTACHMENT_LIMIT
                                                    ? 0.5
                                                    : 1,
                                        }}
                                    >
                                        <Paperclip size={14} strokeWidth={2} />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/markdown,text/csv,application/json,.docx,.xlsx"
                                        style={{ display: 'none' }}
                                        onChange={handleFileInput}
                                    />
                                </>
                            ) : null}
                            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                {searchActive
                                    ? 'Press Enter to start a new search with Lantern'
                                    : 'Press Enter to start a new chat with Lantern'}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canSubmit}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                height: 34,
                                padding: '0 14px',
                                borderRadius: 9999,
                                border: 'none',
                                background: canSubmit ? 'var(--accent)' : 'var(--surface-tertiary)',
                                color: canSubmit ? '#FFFFFF' : 'var(--text-tertiary)',
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: canSubmit ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {creating ? (
                                <>
                                    <Loader2 size={13} strokeWidth={2} className="animate-spin" />
                                    {sendButtonLabel ?? 'Starting…'}
                                </>
                            ) : (
                                <>
                                    Send <CornerDownLeft size={13} strokeWidth={2} />
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {error ? (
                    <div
                        style={{
                            padding: '10px 14px',
                            background: 'var(--bg-danger)',
                            color: 'var(--fg-danger)',
                            borderRadius: 8,
                            fontSize: 13,
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {suggestions.map((s) => (
                        <button
                            key={s.label}
                            type="button"
                            onClick={() => pickSuggestion(s.prompt)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 12px',
                                borderRadius: 9999,
                                border: '1px solid var(--border-default)',
                                background: '#FFFFFF',
                                color: 'var(--text-secondary)',
                                fontSize: 12.5,
                                cursor: 'pointer',
                                transition: 'background 120ms, color 120ms, border-color 120ms',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--border-emphasis)';
                                e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--border-default)';
                                e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                        >
                            <Plus size={12} strokeWidth={2} />
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>
        </main>
    );
}

/* ------------------------------------------------------------------ *
 * TEMP DotMask tuner — remove this whole block (type, defaults, panel)
 * and the <DotMaskTuner/> usage once the backdrop is dialed in.
 * ------------------------------------------------------------------ */
type DotMaskTune = {
    color: string;
    accent: string;
    accentMix: number;
    gap: number;
    dotShape: 'square' | 'circle' | 'rect';
    dotWidth: number;
    dotHeight: number;
    density: number;
    speed: number;
    vignette: number;
    // backdrop geometry (px outward from the textbox card)
    sideSpread: number;
    topOffset: number;
    bottomSpread: number;
    // mask shape (radial ellipse, in %)
    maskRX: number;
    maskRY: number;
    maskCY: number;
    fadeStart: number;
    fadeEnd: number;
    // rainbow backdrop top offset (px) + frosted blur behind the heading (px)
    rainbowTop: number;
    headingBlur: number;
};

const DEFAULT_TUNE: DotMaskTune = {
    color: '#C8C3BA',
    accent: '#C77DAB',
    accentMix: 0.5,
    gap: 6,
    dotShape: 'circle',
    dotWidth: 2,
    dotHeight: 2,
    density: 1.05,
    speed: 2,
    // keep this LOW: DotMask's vignette fades dots from the canvas center,
    // so a high value collapses the spread to ~textbox width. The oval
    // shape/edge is done by the CSS mask below, not by vignette.
    vignette: 0.15,
    // Layer covers all of <main> by default (insets 0); the oval is shaped
    // by the mask radii below. Push these positive to extend past <main>.
    sideSpread: 0,
    topOffset: 0,
    bottomSpread: 50,
    // Centered oval over heading + textbox.
    maskRX: 52,
    maskRY: 40,
    maskCY: 50,
    fadeStart: 45,
    fadeEnd: 82,
    rainbowTop: -345,
    headingBlur: 1,
};

function DotMaskTuner({
    tune,
    setTune,
}: {
    tune: DotMaskTune;
    setTune: Dispatch<SetStateAction<DotMaskTune>>;
}) {
    const [open, setOpen] = useState(true);
    const set = <K extends keyof DotMaskTune>(key: K, value: DotMaskTune[K]) =>
        setTune((prev) => ({ ...prev, [key]: value }));

    const labelStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 11,
        color: '#444',
    };
    const rowStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    };

    const slider = (
        key: keyof DotMaskTune,
        min: number,
        max: number,
        step: number,
    ) => (
        <label style={labelStyle}>
            <span style={rowStyle}>
                <strong style={{ fontWeight: 600 }}>{key}</strong>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {tune[key] as number}
                </span>
            </span>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={tune[key] as number}
                onChange={(e) => set(key, Number(e.target.value) as never)}
            />
        </label>
    );

    return (
        <div
            style={{
                position: 'fixed',
                top: 12,
                right: 12,
                zIndex: 9999,
                width: open ? 240 : 'auto',
                maxHeight: 'calc(100vh - 24px)',
                overflowY: 'auto',
                background: 'rgba(255,255,255,0.96)',
                border: '1px solid #ddd',
                borderRadius: 12,
                boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
                padding: 12,
                fontFamily: 'var(--font-sans)',
                backdropFilter: 'blur(4px)',
            }}
        >
            <div style={{ ...rowStyle, marginBottom: open ? 10 : 0 }}>
                <strong style={{ fontSize: 12 }}>DotMask tuner</strong>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    style={{
                        border: '1px solid #ccc',
                        background: '#fff',
                        borderRadius: 6,
                        fontSize: 11,
                        padding: '2px 8px',
                        cursor: 'pointer',
                    }}
                >
                    {open ? 'hide' : 'show'}
                </button>
            </div>

            {open ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={labelStyle}>
                        <span style={rowStyle}>
                            <strong style={{ fontWeight: 600 }}>dotShape</strong>
                        </span>
                        <select
                            value={tune.dotShape}
                            onChange={(e) =>
                                set('dotShape', e.target.value as DotMaskTune['dotShape'])
                            }
                            style={{ fontSize: 12, padding: '2px 4px' }}
                        >
                            <option value="rect">rect</option>
                            <option value="square">square</option>
                            <option value="circle">circle</option>
                        </select>
                    </label>

                    {slider('gap', 3, 24, 1)}
                    {slider('dotWidth', 1, 16, 1)}
                    {slider('dotHeight', 1, 16, 1)}
                    {slider('speed', 0, 3, 0.1)}
                    {slider('density', 0.2, 2, 0.05)}
                    {slider('vignette', 0, 1, 0.05)}
                    {slider('accentMix', 0, 0.5, 0.01)}

                    <div
                        style={{
                            borderTop: '1px dashed #ddd',
                            margin: '2px 0',
                            paddingTop: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            color: '#999',
                        }}
                    >
                        SHAPE / POSITION
                    </div>
                    {slider('sideSpread', 0, 900, 10)}
                    {slider('topOffset', -100, 300, 5)}
                    {slider('bottomSpread', 0, 900, 10)}
                    {slider('maskRX', 20, 160, 1)}
                    {slider('maskRY', 30, 200, 5)}
                    {slider('maskCY', -50, 100, 5)}
                    {slider('fadeStart', 0, 100, 1)}
                    {slider('fadeEnd', 0, 150, 1)}
                    {slider('rainbowTop', -600, 200, 5)}
                    {slider('headingBlur', 0, 30, 1)}

                    <div style={{ ...rowStyle }}>
                        <label style={{ ...labelStyle, flex: 1 }}>
                            <strong style={{ fontWeight: 600 }}>color</strong>
                            <input
                                type="color"
                                value={tune.color}
                                onChange={(e) => set('color', e.target.value)}
                            />
                        </label>
                        <label style={{ ...labelStyle, flex: 1 }}>
                            <strong style={{ fontWeight: 600 }}>accent</strong>
                            <input
                                type="color"
                                value={tune.accent}
                                onChange={(e) => set('accent', e.target.value)}
                            />
                        </label>
                    </div>

                    <div style={{ ...rowStyle, gap: 6 }}>
                        <button
                            type="button"
                            onClick={() => setTune(DEFAULT_TUNE)}
                            style={{
                                flex: 1,
                                border: '1px solid #ccc',
                                background: '#fff',
                                borderRadius: 6,
                                fontSize: 11,
                                padding: '4px 6px',
                                cursor: 'pointer',
                            }}
                        >
                            reset
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                // eslint-disable-next-line no-console
                                console.error('DotMask tune:', JSON.stringify(tune, null, 2));
                            }}
                            style={{
                                flex: 1,
                                border: '1px solid #ccc',
                                background: '#fff',
                                borderRadius: 6,
                                fontSize: 11,
                                padding: '4px 6px',
                                cursor: 'pointer',
                            }}
                        >
                            log props
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

type HomeChip =
    | {
          kind: 'pending';
          localId: string;
          file: File;
          fileKind: 'image' | 'pdf' | 'text' | 'docx' | 'xlsx';
          isImage: boolean;
          thumbDataUrl: string | null;
      }
    | { kind: 'error'; localId: string; filename: string; message: string };

function HomeChipView({ chip, onRemove }: { chip: HomeChip; onRemove: () => void }) {
    const isError = chip.kind === 'error';
    const filename = chip.kind === 'pending' ? chip.file.name : chip.filename;
    const sizeText = chip.kind === 'pending' ? formatHomeSize(chip.file.size) : null;
    const thumb = chip.kind === 'pending' ? chip.thumbDataUrl : null;
    const fileKind = chip.kind === 'pending' ? chip.fileKind : null;

    const title = isError
        ? `${filename} — ${chip.message}`
        : sizeText
          ? `${filename} (${sizeText})`
          : filename;

    const border = isError
        ? '1px solid var(--fg-danger)'
        : '1px solid var(--border-default)';

    return (
        <span
            title={title}
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 8,
                background: thumb ? '#000' : 'var(--surface-secondary)',
                border,
                overflow: 'hidden',
            }}
        >
            {thumb ? (
                <img
                    src={thumb}
                    alt=""
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : (
                <span
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: isError ? 'var(--fg-danger)' : 'var(--text-secondary)',
                    }}
                >
                    <FileText size={14} strokeWidth={1.75} />
                    <span
                        style={{
                            fontSize: 8.5,
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            lineHeight: 1,
                        }}
                    >
                        {homeBadge(fileKind, filename)}
                    </span>
                </span>
            )}
            <button
                type="button"
                aria-label="Remove"
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: 999,
                    border: 'none',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    cursor: 'pointer',
                    padding: 0,
                }}
            >
                <X size={10} strokeWidth={2.5} />
            </button>
        </span>
    );
}

function homeBadge(
    kind: 'image' | 'pdf' | 'text' | 'docx' | 'xlsx' | null,
    filename: string,
): string {
    if (kind === 'pdf') return 'PDF';
    if (kind === 'docx') return 'DOC';
    if (kind === 'xlsx') return 'XLS';
    if (kind === 'text') {
        const ext = filename.split('.').pop()?.toUpperCase();
        return ext && ext.length <= 4 ? ext : 'TXT';
    }
    const ext = filename.split('.').pop()?.toUpperCase();
    return ext && ext.length <= 4 ? ext : 'FILE';
}

function formatHomeSize(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
